import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CacheService } from './cache.service';

export interface AlertCache {
    alertId: string;
    source: string;
    current?: boolean;
    ticketCategory?: string;
}

@Injectable({
    providedIn: 'root',
})
export class AlertsService {
    private apiServiceUrl = environment.apiUrl;
    private _cache: AlertCache[] = [];
    private alertDataCache: { [key: string]: Observable<any> } = {};

    public alertsData: any;
    public alertDataList: any;
    public fromReleatedAlerts = false;
    public currentAlertId!: string;
    public loadAlertsData = new BehaviorSubject<boolean>(false);
    public _source!:string;
    viewPotentialReason=new BehaviorSubject<any>(null)

    constructor(
      private http: HttpClient,
      private cacheService: CacheService
    ) { }


    // private getCacheTTL(): number {
    //   return environment.cacheTTLSeconds * 1000;
    // }
    private getCacheTTL(): number {
      const fallback = 90_000; // 90 seconds
      const ttl = (environment as any)?.cacheTTLSeconds;
      return typeof ttl === 'number' ? ttl * 1000 : fallback;
    }



    getCachedTabData(category: 'incident' | 'problem' | 'catalog'): any[] | null {
      return this.cacheService.get<any[]>(category);
    }

    setCachedTabData(category: 'incident' | 'problem' | 'catalog', data: any[]): void {
      this.cacheService.set(category, data);
    }

    invalidateTabData(category: 'incident' | 'problem' | 'catalog'): void {
      this.cacheService.remove(category);
    }

    get cache(): AlertCache[] {
        return this._cache;
    }

    public setCache(data: AlertCache[]): void {
        this._cache = data;
    }

    public setSource(source:string){
        this._source=source
    }

    public getSource(){
        return this._source;
    }
    public addCurrentToCache(selectedData: AlertCache): void {
        const enrichedData: AlertCache = {
          alertId: selectedData.alertId,
          source: selectedData.source,
          ticketCategory: selectedData.ticketCategory || localStorage.getItem('ticketCategory') || 'incident',
          current: true,
        };

        const existingIndex = this._cache.findIndex(item => item.alertId === enrichedData.alertId);
        if (existingIndex >= 0) {
          this._cache[existingIndex] = enrichedData;
        } else {
          this._cache.push(enrichedData);
        }

        this._cache.forEach(item => {
          if (item.alertId !== enrichedData.alertId) {
            item.current = false;
          }
        });
      }


    public removeAlertFromCache(alertId: string): void {
        this._cache = this._cache.filter(item => item.alertId !== alertId);
    }

    public removeCache(): void {
        this._cache = [];
        this.alertDataCache = {};
    }

    public setloadAlertsData(isReload = false): void {
        this.loadAlertsData.next(isReload);
    }
    public getJiraSummary(alertId: string, source: string): Observable<any> {
        const ticketCategory = 'incident';
        const cacheKey = `${alertId}_${source}_${ticketCategory}`;

        if (!this.alertDataCache[cacheKey]) {
          const url = `${this.apiServiceUrl}/ava/aipo/report/incident`;
          const params = { issueId: alertId, source };

          this.alertDataCache[cacheKey] = this.http.get(url, { params }).pipe(
            tap(response => {
              this.alertsData = response;
              this.addCurrentToCache({
                alertId,
                source,
                ticketCategory,
              });
            }),
            shareReplay(1)
          );
        }

        return this.alertDataCache[cacheKey];
      }

      public getAlertSummary(alertId: string, source: string, ticketCategory: string): Observable<any> {
        const normalizedCategory = ticketCategory.toLowerCase();
        const cacheKey = `${alertId}_${source}_${normalizedCategory}`;

        if (!this.alertDataCache[cacheKey]) {
          let url = '';
          let params: any = { issueId: alertId };

          switch (normalizedCategory) {
            case 'problem':
              url = `${this.apiServiceUrl}/ava/aipo/report/problem-ticket`;
              // params.Source = source;
              break;
            case 'catalog':
              url = `${this.apiServiceUrl}/ava/aipo/report/catalog-ticket`;
              // params.Source = source;
              break;
            default:
              url = `${this.apiServiceUrl}/ava/aipo/report/incident`;
              params.source = source;
              break;
          }

          const request$ = this.http.get<{ alert: any }>(url, { params });

          this.alertDataCache[cacheKey] = request$.pipe(
            tap((response) => {
              if (response?.alert) {
                response.alert.ticketCategory = normalizedCategory;
                this.alertsData = response;
                this.addCurrentToCache({
                  alertId,
                  source,
                  ticketCategory: normalizedCategory,
                });
              }
            }),
            shareReplay(1)
          );
        }

        return this.alertDataCache[cacheKey];
      }
      public getProblemTicketSummary(alertId: string, source: string): Observable<any> {
        const url = `${this.apiServiceUrl}/ava/aipo/report/problem-ticket`;
        const params = { issueId: alertId };
        return this.http.get(url, { params });
      }

      public getCatalogTaskSummary(alertId: string, source: string): Observable<any> {
        const url = `${this.apiServiceUrl}/ava/aipo/report/catalog-ticket`;
        const params = { issueId: alertId };
        return this.http.get(url, { params });
      }

    public getSnowAlertsData(): Observable<any> {
        const url = `${this.apiServiceUrl}/ava/aipo/report/incidents`;
        return this.http.get(url);
    }

    public getJiraAlertsData(): Observable<any> {
        const url = `${this.apiServiceUrl}/aipo/platform/api/jira/connector/issue/list`;
        return this.http.get(url);
    }

    public getCauseAndSolutionSummary(alertId: string, source: string): Observable<any> {
        const url = `${this.apiServiceUrl}/ava/aipo/report/incident`;
        const params = { issueId: alertId, source };
        return this.http.get(url, { params });
    }

    public getDependencyMapping(alertSysId: string ,incidentId:string): Observable<any> {
        const url = `${this.apiServiceUrl}/ava/aipo/platform/api/snow/connector/cmdbRelation`;
        const params = { sys_id: alertSysId ,incidentId};
        return this.http.get(url, { params });
    }

    get releatedAlertIds(): string[] {
        return this.alertDataList?.relatedAlert?.map((alert: any) => alert.IncidentId) || [];
    }

    public toggleFeedbackStatus(alertId: string, feedback: string): Observable<any> {
        const url = `${this.apiServiceUrl}/ava/aipo/report/incident/feedback`;
        const params = { incidentId: alertId, feedback };
        return this.http.put(url, {}, { params });
    }

    public getGenerateGenAIResponse(incidentId: string): Observable<any> {
        const url = `${this.apiServiceUrl}/aipo/domain/api/remediation/regenerate`;
        const params = { incidentId };
        return this.http.get(url, { params });
    }

    public clearCache(alertId: string, source: string): void {
        const cacheKey = `${alertId}_${source}`;
        delete this.alertDataCache[cacheKey];
    }
    public getProblemTicketData(): Observable<any> {
        const url = `${this.apiServiceUrl}/ava/aipo/report/problem/ticket`;
        return this.http.get(url);
      }
      public getCatalogTaskData(): Observable<any> {
        const url = `${this.apiServiceUrl}/ava/aipo/report/snow-sc-tasks`;
        return this.http.get(url);
      }
      // getProblemStatistics(prbTicketId: string): Observable<any> {
      //   const url = `${this.apiServiceUrl}/ava/aipo/report/problemTicket/statistics?prbTicketId=${prbTicketId}`;
      //   return this.http.get(url);
      // }

getProblemStatistics(prbTicketId: string): Observable<any> {
  const url = `${this.apiServiceUrl}/ava/aipo/report/problemTicket/statistics?prbTicketId=${prbTicketId}`;
  return this.http.get(url).pipe(
    map((response: any) => {
      if (response && response.timeStatistics) {
        const transformed = {
          ...response,
          timeStatistics: {
            daily: {
              counts: response.timeStatistics.daily?.counts || {},
              start_dateRange: response.timeStatistics.daily?.start_dateRange || '',
              end_dateRange: response.timeStatistics.daily?.end_dateRange || ''
            },
            weekly: {
              counts: response.timeStatistics.weekly?.counts || {}
            },
            hourly: {
              counts: response.timeStatistics.hourly?.counts || {},
              start_dateRange: response.timeStatistics.hourly?.start_dateRange || '',
              end_dateRange: response.timeStatistics.hourly?.end_dateRange || ''
            }
          }
        };
        return transformed;
      }
      return response;
    }),
    shareReplay(1)
  );
}

    }